import { z } from "zod";
import { ADMIN_CLASS_USE_MOCK } from "../config/admin.config";
import { CLASS_LIMITS } from "../constants/class.constants";
import { trimmedOptionalString, trimmedRequiredDescriptionString, trimmedRequiredTitleString } from "@/utils/zodString";

const benefitSchema = z.object({
  id: z.string(),
  title: trimmedRequiredTitleString({
    requiredMessage: "Title is required",
    max: CLASS_LIMITS.benefitTitleMax,
  }),
  description: trimmedRequiredDescriptionString({
    requiredMessage: "Description is required",
    max: CLASS_LIMITS.benefitDescriptionMax,
  }),
});

const locationSchema = z.object({
  address: z.string().optional(),
  district: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  pincode: z.string().optional(),
});

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

function requiredNumber<T extends z.ZodTypeAny>(
  schema: T,
  message: string
) {
  return z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? undefined : value),
    schema.refine((val) => val !== undefined && val !== null, { message })
  );
}

export const classFormSchema = z.object({
  name: trimmedRequiredTitleString({
    requiredMessage: "Class Name is required",
    max: CLASS_LIMITS.nameMax,
    maxMessage: `Class name cannot exceed ${CLASS_LIMITS.nameMax} characters`,
  }),
  description: trimmedRequiredDescriptionString({
    requiredMessage: "Class Description is required",
    max: CLASS_LIMITS.descriptionMax,
    maxMessage: `Description cannot exceed ${CLASS_LIMITS.descriptionMax} characters`,
  }),
  validityDays: requiredNumber(
    z.coerce.number({ invalid_type_error: "Validity is required" })
      .int()
      .positive("Validity is required"),
    "Validity is required"
  ),
  price: requiredNumber(
    z.coerce.number({ invalid_type_error: "Class Price is required" })
      .int("Class Price must be a whole number")
      .nonnegative("Class Price is required"),
    "Class Price is required"
  ),
  taxPercent: requiredNumber(
    z.coerce.number({ invalid_type_error: "Tax percentage is required" })
      .int("Tax percentage must be a whole number")
      .min(0, "Tax percentage is required")
      .max(100, "Tax cannot exceed 100%"),
    "Tax percentage is required"
  ),
  level1Id: z.string().min(1, "Level 1 is required"),
  level2Id: z.string().min(1, "Level 2 is required"),
  level3Id: z.string().min(1, "Level 3 is required"),
  benefits: z
    .array(benefitSchema)
    .min(1, "Add at least one benefit")
    .max(
      CLASS_LIMITS.benefitsMax,
      `You can add up to ${CLASS_LIMITS.benefitsMax} benefits`
    ),
  schoolId: z.string().optional(),
  schoolName: trimmedOptionalString({ max: 200 }).optional(),
  adminName: trimmedOptionalString({ max: 200 }).optional(),
  schoolTitle: trimmedOptionalString({ max: 200 }).optional(),
  email: trimmedOptionalString({ max: 254 }).optional(),
  contactNumber: trimmedOptionalString({ max: 20 }).optional(),
  licenseCountTeacher: z.coerce.number().nonnegative().optional(),
  licenseCountStudent: z.coerce.number().nonnegative().optional(),
  courseId: z.string().optional(),
  location: locationSchema.optional(),
  thumbnailUrl: z.string().min(1, "Thumbnail image is required"),
  uploadId: z.string().min(1, "Thumbnail upload is required"),
}).superRefine((data, ctx) => {
  const schoolId = data.schoolId?.trim();

  if (!ADMIN_CLASS_USE_MOCK) {
    if (!schoolId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "School is required",
        path: ["schoolId"],
      });
    } else if (!isValidUuid(schoolId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select a valid school",
        path: ["schoolId"],
      });
    }

    const courseId = data.courseId?.trim();
    if (!courseId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Course is required",
        path: ["courseId"],
      });
    } else if (!isValidUuid(courseId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select a valid course",
        path: ["courseId"],
      });
    }
    return;
  }

  if (!data.schoolName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "School name is required",
      path: ["schoolName"],
    });
  }
  if (!data.adminName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "School admin name is required",
      path: ["adminName"],
    });
  }
  if (!data.email?.trim() || !z.string().email().safeParse(data.email).success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Enter a valid email address",
      path: ["email"],
    });
  }
  if (!data.contactNumber?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Contact number is required",
      path: ["contactNumber"],
    });
  }
  if (!data.courseId?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Course is required",
      path: ["courseId"],
    });
  }
});

export type ClassFormSchemaInput = z.input<typeof classFormSchema>;
export type ClassFormSchemaValues = z.output<typeof classFormSchema>;
